import React, { cloneElement, isValidElement, useState } from 'react';
import { string } from 'yup';
import cx from 'classnames';

// components
import {
  SelectWithController,
  TextArea,
  TextInput,
} from 'src/components/Inputs';
import { Btn as NewButton } from 'src/components-v2/Inputs';
import { MarketoForm, GdprFields } from 'src/components/Forms';
import { PhoneField } from 'src/components/Forms/PhoneField';
import { RichTextRenderer } from 'src/components/RichTextRenderer';
import { Grid, Column } from 'src/components/Layout/Grid';
import { formCtaAdapter } from 'src/components/FormCta';
import EmployeeCount from 'src/components/Inputs/EmployeeCount/EmployeeCount';
import { FormWrapper } from './FormWrapper';

// contexts
import { usePageLocale } from 'src/contexts';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';

// helpers
import {
  getRequiredMessage,
  isBlackBackgroundColor,
  mutateHardCodedLinks,
} from 'src/lib/utils';

// types
import { Cta } from 'src/components/cta.types';

import styles from './CmsFormWithAssetHero.module.scss';

import { CmsThemes, Locales } from 'src/lib/constants';
import { sanitizeHtml } from 'src/lib/html-utils';

import * as enUsForm from 'src/store/form';
import * as enEuForm from 'src/store/en-eu/form';
import * as enGbForm from 'src/store/en-gb/form';

const formPerLocale = {
  [Locales.EN_US]: enUsForm,
  [Locales.EN_EU]: enEuForm,
  [Locales.EN_GB]: enGbForm,
};
const CmsFormWithAssetHero = ({
  fields,
  sys,
  component,
}: {
  fields;
  sys;
  component?: HeroElement;
}): React.ReactElement => {
  const locale = usePageLocale();
  const cmsTheme = useCmsTheme();
  const isWeb3 = cmsTheme === CmsThemes.WEB3;

  const backgroundColor = fields?.backgroundColor || 'white';
  const isDarkBackground = isBlackBackgroundColor(backgroundColor);
  // on web 3.0 fields keep their light styling on dark backgrounds, so the
  // dark treatment (classes AND the isDarkMode prop threads inputs consume
  // internally, e.g. the react-select menu) only applies on web 2.0
  const fieldDarkMode = isDarkBackground && !isWeb3;
  const formWrapperProps = {
    component,
    body: fields?.leftColumn,
    backgroundColor,
    mode: fields?.mode,
    asset: fields?.asset?.fields,
  };

  // on web 3.0 labels move inside the fields as placeholders, per the Web
  // Design System Figma
  const inputGroupClassName = cx(
    styles.inputGroup,
    isWeb3 ? styles.inputGroup_web3 : fieldDarkMode && styles.inputGroup_dark,
  );
  const selectGroupClassName = cx(
    styles.selectGroup,
    isWeb3 ? styles.selectGroup_web3 : fieldDarkMode && styles.selectGroup_dark,
  );
  const textAreaGroupClassName = cx(
    styles.textAreaGroup,
    isWeb3
      ? styles.textAreaGroup_web3
      : fieldDarkMode && styles.textAreaGroup_dark,
  );
  const disclaimerClassName = cx(styles['contact-form__disclaimer'], {
    [styles['contact-form__disclaimer_web3']]: isWeb3,
    [styles['contact-form__disclaimer_dark']]: fieldDarkMode,
  });
  const getPlaceholder = (label?: string) => {
    return isWeb3 ? label : undefined;
  };

  // form
  const {
    id = 'contactForm',
    'include-phone': includePhone = false,
    'action-text': actionText = 'Contact sales',
    emailLabelOverride = 'Company email',
    companyLabelOverride = 'Company name',
    'marketo-key': marketoKey,
    includeCompany = false,
    includeJobTitle = false,
    includeCommentsField = false,
    includeDefaultFields = true,
    includeNames = true,
    includePxFieldset = false,
    includeState = true,
    children = null,
    disclaimer = '',
    trackingId = 'MARKETO_FORM',
    postSubmitContent = null,
    hasOnPagePostSubmitContent = false,
    isBodyForm = false,
    shouldForward = true,
    marketoFormBtnUrl,
    redirectOnSubmit,
    redirectUrl,
    formType,
    commentsFieldPlaceholder,
    rememberEmail = false,
    defaultCountryFromTimeZone = false,
  }: Cta = formCtaAdapter({
    fields,
    template: 'Hero Form',
  });
  const { formData } = mutateHardCodedLinks({
    data: formPerLocale[locale] || formPerLocale[Locales.EN_US],
    locale,
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const shouldRenderPostSubmitContent =
    hasOnPagePostSubmitContent && isFormSubmitted;

  // inject children with props for use in form
  const childrenWithProps = ({ control, errors, register, setValue }) => {
    return React.Children.map(children, (child) => {
      // checking if child is invalid for safety, also avoids a typescript error
      if (!isValidElement(child)) {
        return child;
      }
      const props = {
        register: register,
        required: child.props.errorMessage,
        errorMessage: errors[child.props.name]?.message,
        control: control,
        setValue: setValue,
        isDarkMode: fieldDarkMode,
      };
      // Note: this is will be deprecated with SITE-2278
      return cloneElement(child, props);
    });
  };

  const handleSubmit = () => {
    setIsFormSubmitted(true);
  };

  const formProps = {
    actionText,
    companyLabelOverride,
    emailLabelOverride,
    children,
    includeDefaultFields,
    includePhone,
    isDarkMode: fieldDarkMode,
    marketoKey,
    trackingId,
    locale,
    formType,
    shouldForward,
    rememberEmail,
    redirectUrl:
      // we don't pass a url if the form should stay on the page
      // otherwise we try to pass the redirect url from gated content
      // or the marketo form button so that the user gets a predictable experience
      hasOnPagePostSubmitContent
        ? undefined
        : redirectOnSubmit
        ? redirectUrl
        : marketoFormBtnUrl,

    onSubmit: handleSubmit,
    render: ({ control, register, errors, setValue, getValues }) => {
      return (
        <>
          {includeDefaultFields && (
            <>
              {includeNames && (
                <div
                  className={cx(
                    'grid-container full',
                    isWeb3 && styles.namesRow_web3,
                  )}
                >
                  <div className='grid-x form-group'>
                    <div className='cell small-12 medium-6'>
                      <TextInput
                        className={inputGroupClassName}
                        labelClassName={styles.label}
                        inputClassName={styles.input}
                        label={formData.firstName.label}
                        placeholder={getPlaceholder(formData.firstName.label)}
                        id={formData.firstName.name}
                        name={formData.firstName.name}
                        describedby={formData.firstName.describedby}
                        isDarkMode={fieldDarkMode}
                        errorMessage={
                          errors?.[formData.firstName.name]?.message
                        }
                        register={register}
                        required={getRequiredMessage(formData.firstName.label)}
                        autoComplete={formData.firstName.autoComplete}
                      />
                    </div>

                    <div className='cell small-12 medium-6'>
                      <TextInput
                        className={inputGroupClassName}
                        labelClassName={styles.label}
                        inputClassName={styles.input}
                        label={formData.lastName.label}
                        placeholder={getPlaceholder(formData.lastName.label)}
                        id={formData.lastName.name}
                        name={formData.lastName.name}
                        describedby={formData.lastName.describedby}
                        isDarkMode={fieldDarkMode}
                        errorMessage={errors?.[formData.lastName.name]?.message}
                        register={register}
                        required={getRequiredMessage(formData.lastName.label)}
                        autoComplete={formData.lastName.autoComplete}
                      />
                    </div>
                  </div>
                </div>
              )}

              {includeCompany && (
                <TextInput
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  label={companyLabelOverride}
                  placeholder={getPlaceholder(companyLabelOverride)}
                  id={formData.company.name}
                  name={formData.company.name}
                  describedby={formData.company.describedby}
                  isDarkMode={fieldDarkMode}
                  errorMessage={errors?.[formData.company.name]?.message}
                  register={register}
                  required={getRequiredMessage(formData.company.label)}
                  autoComplete={formData.company.autoComplete}
                />
              )}

              <TextInput
                className={inputGroupClassName}
                labelClassName={styles.label}
                inputClassName={styles.input}
                label={emailLabelOverride}
                placeholder={getPlaceholder(emailLabelOverride)}
                id={formData.email.name}
                name={formData.email.name}
                describedby={formData.email.describedby}
                isDarkMode={fieldDarkMode}
                errorMessage={errors?.Email?.message}
                register={register}
                pattern={{
                  value: string().email().tests[0].OPTIONS.params.regex,
                  message: formData.email.invalidMessage,
                }}
                required={getRequiredMessage(formData.email.label)}
                autoComplete={formData.email.autoComplete}
                defaultValue={getValues(formData.email.name)}
              />

              {/*
                NOTE: typically the hero form is used for white papers so this may never render
                however, there's always a chance someone full-send YOLO's and puts a contact form (2006 or 2007)
                here
              */}
              <EmployeeCount
                className={selectGroupClassName}
                controlClassName={styles.selectControl}
                inputClassName={styles.selectInput}
                labelClassName={styles.selectLabel}
                control={control}
                errors={errors}
                locale={locale}
                marketoKey={marketoKey}
                register={register}
                setValue={setValue}
                usePlaceholderLabels={isWeb3}
              />

              {includePhone && (
                <PhoneField
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  data={formData.phone}
                  getPlaceholder={getPlaceholder}
                  isDarkMode={fieldDarkMode}
                  errors={errors}
                  register={register}
                />
              )}

              {includePxFieldset && (
                <fieldset>
                  <SelectWithController
                    className={selectGroupClassName}
                    controlClassName={styles.selectControl}
                    inputClassName={styles.selectInput}
                    labelClassName={styles.selectLabel}
                    placeholder={getPlaceholder(formData.categoryInput?.label)}
                    {...formData.categoryInput}
                    errorMessage={
                      errors?.[formData.categoryInput?.name]?.message
                    }
                    required={getRequiredMessage(formData.categoryInput?.label)}
                    isDarkMode={fieldDarkMode}
                    control={control}
                    register={register}
                    setValue={setValue}
                  />

                  <TextInput
                    className={inputGroupClassName}
                    labelClassName={styles.label}
                    inputClassName={styles.input}
                    placeholder={getPlaceholder(
                      formData.coreProviderInput?.label,
                    )}
                    {...formData.coreProviderInput}
                    errorMessage={
                      errors?.[formData.coreProviderInput?.name]?.message
                    }
                    required={getRequiredMessage(
                      formData.coreProviderInput?.label,
                    )}
                    isDarkMode={fieldDarkMode}
                    register={register}
                  />

                  <TextInput
                    className={inputGroupClassName}
                    labelClassName={styles.label}
                    inputClassName={styles.input}
                    placeholder={getPlaceholder(
                      formData.bankingProviderInput?.label,
                    )}
                    {...formData.bankingProviderInput}
                    errorMessage={
                      errors?.[formData.bankingProviderInput?.name]?.message
                    }
                    required={getRequiredMessage(
                      formData.bankingProviderInput?.label,
                    )}
                    isDarkMode={fieldDarkMode}
                    register={register}
                  />
                </fieldset>
              )}

              {includeJobTitle && (
                <TextInput
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  label={formData.jobTitle?.label}
                  placeholder={getPlaceholder(formData.jobTitle?.label)}
                  id={formData.jobTitle?.name}
                  name={formData.jobTitle?.name}
                  describedby={formData.jobTitle?.describedby}
                  errorMessage={errors?.[formData.jobTitle?.name]?.message}
                  register={register}
                  required={getRequiredMessage(formData.jobTitle?.label)}
                  autoComplete={formData.jobTitle?.autoComplete}
                  isDarkMode={fieldDarkMode}
                />
              )}

              {includeCommentsField && (
                <TextArea
                  className={textAreaGroupClassName}
                  {...formData.comments}
                  placeholder={
                    commentsFieldPlaceholder || formData.comments?.placeholder
                  }
                  isLabelVisible={!isWeb3}
                  isDarkMode={fieldDarkMode}
                  errorMessage={errors?.[formData.comments?.name]?.message}
                  required={getRequiredMessage(formData.comments?.label)}
                  register={register}
                />
              )}
            </>
          )}

          {childrenWithProps({
            control,
            register,
            errors,
            setValue,
          })}

          <GdprFields
            className={selectGroupClassName}
            usePlaceholderLabels={isWeb3}
            controlClassName={styles.selectControl}
            inputClassName={styles.selectInput}
            labelClassName={styles.selectLabel}
            consentCheckboxClassName={cx(
              styles.checkboxConteiner,
              isDarkBackground && styles.checkboxConteiner_dark,
            )}
            control={control}
            errors={errors}
            includeState={includeState}
            isDarkMode={fieldDarkMode}
            register={register}
            setValue={setValue}
            defaultCountryFromTimeZone={defaultCountryFromTimeZone}
          />

          <Grid noPadding noMarginRightMedium noMarginRightLarge useThreadsGrid>
            <Column medium={isBodyForm ? 6 : 12} allowOverflow>
              <NewButton
                className={cx(styles.button, isWeb3 && styles.button_web3)}
                type='submit'
                value={actionText}
                variant='primary'
                backgroundColor={backgroundColor}
              >
                {actionText}
              </NewButton>
            </Column>
          </Grid>

          {disclaimer ? (
            typeof disclaimer === 'string' ? (
              <p
                data-testid='disclaimer'
                className={disclaimerClassName}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(disclaimer),
                }}
              />
            ) : (
              <RichTextRenderer
                content={disclaimer}
                className={disclaimerClassName}
              />
            )
          ) : // Don't use fallback disclaimer text on Body Form CTA instances.
          !isBodyForm ? (
            <p
              data-testid='disclaimer'
              className={disclaimerClassName}
              dangerouslySetInnerHTML={{
                __html: formData.disclaimer,
              }}
            />
          ) : (
            ''
          )}
        </>
      );
    },
  };

  return (
    <FormWrapper id={id} sys={sys} {...formWrapperProps}>
      <>
        {shouldRenderPostSubmitContent ? (
          postSubmitContent
        ) : (
          <MarketoForm {...formProps} />
        )}
      </>
    </FormWrapper>
  );
};

export default CmsFormWithAssetHero;
